/** @jsxImportSource @emotion/react */
import Button from 'react-bootstrap/Button'
import Toast from 'components/atoms/Toast'
import { UserPermission } from 'components/lab/user/permissions'
import { confirm } from 'components/molecules/Confirmation'
import { useAppSelector } from 'hooks/redux'
import { useUpdateInsuranceVerificationFlagsMutation } from 'store/services/labOrder'
import { Order } from 'store/types'
import {
  EditablePayer,
  EligibilityFlagKey,
  getEditablePayers,
} from './outsourceVisibility'

const statusLabel = (value: boolean | null): string =>
  value === true ? 'detected' : value === false ? 'no' : 'not determined'

/**
 * Rendered inside the Insurance section. For every payer whose latest verification we can write to,
 * it shows each Medicare Advantage / Medicaid flag's current state and (for users allowed to edit
 * flags) a toggle behind a confirmation modal. A flag is editable even when it is `null`
 * (undetermined — COX/PSG, an AI-disabled payer, or a failed lambda): the policy is strictly
 * fail-closed, so a null flag hides the order and must be clearable to `false` to grant access.
 * Only when every payer's flags are explicitly `false` does the order become visible to outsource;
 * the backend policy remains the sole enforcement point.
 */
export const InsuranceEligibilityFlagControls = ({ order }: { order?: Order }) => {
  const orderId = Number(order?.id)
  const canModifyFlags = useAppSelector(
    (state) =>
      state.init.isSuperadmin === true ||
      state.init.permissions.includes(UserPermission.MODIFY_INSURANCE_ELIGIBILITY_FLAGS),
  )
  const [updateFlags, { isLoading }] = useUpdateInsuranceVerificationFlagsMutation()

  // Only users who may edit flags (or superadmins) see this control at all — there's nothing
  // actionable here for anyone else, and the backend rejects the mutation regardless.
  if (!canModifyFlags) {
    return null
  }

  const editablePayers = getEditablePayers(order)
  if (editablePayers.length === 0) {
    return null
  }

  const setFlag = async (
    payer: EditablePayer,
    key: EligibilityFlagKey,
    label: string,
    nextValue: boolean,
  ) => {
    try {
      await confirm('', {
        title: `Mark ${payer.name} as ${nextValue ? '' : 'not '}${label}?`,
        description: nextValue
          ? `This marks ${payer.name} as ${label}, which hides the order from outsource users.`
          : `This records that ${payer.name} is not ${label}. Once every payer is cleared of Medicare Advantage and Medicaid, outsource users will be able to see this order.`,
        yesAction: 'Confirm',
        noAction: 'Cancel',
        yesColor: 'primary',
      })
    } catch {
      return
    }

    try {
      await updateFlags({
        orderId,
        insuranceVerificationId: payer.verification.uuid,
        [key]: nextValue,
      }).unwrap()
      Toast({ type: 'success', label: `Updated ${label} flag for ${payer.name}.` })
    } catch (err: any) {
      Toast({ type: 'error', label: err?.data?.message || `Failed to update ${label} flag.` })
    }
  }

  return (
    <div
      className="w-100 mb-3"
      css={{
        padding: '12px 16px',
        borderRadius: 8,
        border: '1px solid #d7dde3',
        background: '#f6f8fa',
        color: '#3d4a56',
        fontSize: 13,
      }}>
      <div css={{ fontWeight: 700, color: '#33404c', marginBottom: 8 }}>
        Medicare Advantage / Medicaid classification
      </div>
      {editablePayers.map((payer) => (
        <div
          key={payer.verification.uuid}
          css={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            padding: '6px 0',
            borderTop: '1px solid #e3e8ec',
          }}>
          <div css={{ flex: '1 1 260px' }}>
            <b>{payer.name}</b> —{' '}
            {payer.flags.map((flag, i) => (
              <span key={flag.key}>
                {i > 0 && ' · '}
                {flag.label}: {statusLabel(flag.value)}
              </span>
            ))}
          </div>
          <div css={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {payer.flags.map((flag) => {
              // A flag blocks outsource visibility unless it is explicitly `false`, so `true` and
              // `null` both offer "clear to false" (grant); only `false` offers re-marking `true`.
              const nextValue = flag.value === false
              return (
                <Button
                  key={flag.key}
                  type="button"
                  size="sm"
                  variant={flag.value === false ? 'outline-secondary' : 'outline-primary'}
                  disabled={isLoading}
                  onClick={() => setFlag(payer, flag.key, flag.label, nextValue)}>
                  {nextValue ? `Mark as ${flag.label}` : `Mark as not ${flag.label}`}
                </Button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
